import { observer } from 'mobx-react'
import { useContext } from 'react'
import type { ReactNode } from 'react'
import { Button, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { cx } from '@/utils/cx'
import type {
  Answers,
  GroupItem,
  GroupQuestion,
  ReviewEntry,
  ReviewScreen as ReviewScreenType,
  ScalarAnswerValue,
  ScalarQuestion,
  Screen,
} from '@/types/dataCollection'
import { ScreenHeader } from './ScreenHeader'
import { HelpSection } from './HelpSection'
import { formatAnswerForReview, isGroupQuestion, isQuestionVisible, resolveLabel } from './helpers'

interface ReviewScreenProps {
  screen: ReviewScreenType
  /** The screens behind the user (the back stack): every entry's screen is among them. */
  screensById: ReadonlyMap<string, Screen>
  savedAnswers: Answers
  /** Rendered under the screen header, e.g. the retry notice. */
  notice?: ReactNode
  submitting: boolean
  /** The section's edit button: jump to the given screen, then return here. */
  onEdit: (screenId: string) => void
  onSubmit: () => void
}

interface Row {
  key: string
  label: string
  value: string
}

interface GroupCard {
  key: string
  title?: string
  rows: Row[]
}

function isScalarAnswer(value: unknown): value is ScalarAnswerValue {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

function scalarRow(
  question: ScalarQuestion,
  value: ScalarAnswerValue,
  language: string,
  key: string
): Row {
  return {
    key,
    label: resolveLabel(question.label, language),
    value: formatAnswerForReview(question, value, language),
  }
}

/**
 * One sub-card per group item: the first inner select's option label as the title (e.g. the type
 * of obligation), every other answered inner question as a row.
 */
function groupCards(question: GroupQuestion, items: GroupItem[], language: string): GroupCard[] {
  const titleQuestion = question.questions.find((inner) => inner.type === 'select')
  return items.map((item, index) => {
    const rows: Row[] = []
    for (const inner of question.questions) {
      if (inner.id === titleQuestion?.id) continue
      if (!isQuestionVisible(inner, item)) continue
      const value = item[inner.id]
      if (!isScalarAnswer(value)) continue
      rows.push(scalarRow(inner, value, language, `${question.id}.${index}.${inner.id}`))
    }
    const titleValue =
      titleQuestion && isQuestionVisible(titleQuestion, item) ? item[titleQuestion.id] : undefined
    return {
      key: `${question.id}.${index}`,
      title:
        titleQuestion && isScalarAnswer(titleValue)
          ? formatAnswerForReview(titleQuestion, titleValue, language)
          : undefined,
      rows,
    }
  })
}

export const ReviewScreen = observer(
  ({
    screen,
    screensById,
    savedAnswers,
    notice,
    submitting,
    onEdit,
    onSubmit,
  }: ReviewScreenProps) => {
    const store = useContext(StoreContext)
    const { isMobile } = store.AppState
    const { language } = store.TranslationsState
    const { dataCollectionPage: trans } = store.TranslationsState.translations

    const renderEditButton = (screenId: string) =>
      screensById.has(screenId) && (
        <button
          type="button"
          className="data-collection__review-edit"
          onClick={() => onEdit(screenId)}
          disabled={submitting}
          data-testid={`data-collection-review-edit-${screenId}`}
        >
          {resolveLabel(screen.editLabel, language)}
        </button>
      )

    // A section with several entries (one per person in the two-partner flows) gets an edit
    // button per card, so each person's screen can be reached; a single entry is edited from the
    // section heading.
    const renderEntry = (entry: ReviewEntry, withEditButton: boolean) => {
      const source = screensById.get(entry.screenId)
      if (!source) return null
      const questions =
        source?.type === 'questions'
          ? source.questions.filter(
              (question) =>
                isQuestionVisible(question, savedAnswers) && savedAnswers[question.id] !== undefined
            )
          : []
      const title = entry.title ? resolveLabel(entry.title, language) : ''

      return (
        <div className="data-collection__review-card" key={entry.screenId}>
          {(title || withEditButton) && (
            <div className="data-collection__review-card-head">
              <span className="data-collection__review-card-title">{title}</span>
              {withEditButton && renderEditButton(entry.screenId)}
            </div>
          )}
          {questions.map((question) => {
            const value = savedAnswers[question.id]
            if (isGroupQuestion(question)) {
              const items = Array.isArray(value) ? value : []
              if (items.length === 0) return null
              // A repeatable group (further sources of income, ongoing obligations): the group's
              // label as a heading, then one sub-card per item.
              return (
                <div className="data-collection__review-group" key={question.id}>
                  <span className="data-collection__review-group-label">
                    {resolveLabel(question.label, language)}
                  </span>
                  {groupCards(question, items, language).map((card) => (
                    <div className="data-collection__review-item" key={card.key}>
                      {card.title && (
                        <span className="data-collection__review-item-title">{card.title}</span>
                      )}
                      {card.rows.map((row) => (
                        <div className="data-collection__review-row" key={row.key}>
                          <span className="data-collection__review-row-label">{row.label}</span>
                          <span className="data-collection__review-row-value">{row.value}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )
            }
            if (!isScalarAnswer(value)) return null
            const row = scalarRow(question, value, language, question.id)
            return (
              <div className="data-collection__review-row" key={row.key}>
                <span className="data-collection__review-row-label">{row.label}</span>
                <span className="data-collection__review-row-value">{row.value}</span>
              </div>
            )
          })}
        </div>
      )
    }

    return (
      <div className="page-container mobile-m-0">
        <div className="layout__content-container-with-scroll">
          <ScreenHeader title={screen.title} description={screen.description} icon={screen.icon} />
          {notice}

          {screen.sections.map((section, index) => {
            const editPerEntry = section.entries.length > 1
            const sectionEditScreenId = editPerEntry ? undefined : section.entries[0]?.screenId
            return (
              <div className="data-collection__review-section" key={index}>
                <div className="data-collection__review-section-head">
                  <span className="data-collection__review-section-title">
                    {resolveLabel(section.title, language)}
                  </span>
                  {sectionEditScreenId && renderEditButton(sectionEditScreenId)}
                </div>
                {section.entries.map((entry) => renderEntry(entry, editPerEntry))}
              </div>
            )
          })}

          {screen.help && <HelpSection help={screen.help} />}
        </div>

        {/* No back button: the only ways on are editing a section or confirming the answers. */}
        <div className="layout__content-container-bottom-fixed">
          {!isMobile && <div className="layout__content-container-bottom-left" />}
          <Button onClick={onSubmit} disabled={submitting} dataTestid="data-collection-next">
            {submitting && <LoadingSpinner inButton />}
            <span
              className={cx('generic__button-text', submitting && 'generic__button-text--loading')}
            >
              {trans.next}
            </span>
          </Button>
        </div>
        <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
      </div>
    )
  }
)
